import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import FloatTooltip from 'ui-library/floating-ui/Tooltip';

export enum MethodEditorTabEditingState {
  NOT_EDITING = 'NOT_EDITING',
  // To handle the lag between the user clicking Edit and the tables being unlocked for edits
  STARTING_EDITING = 'STARTING_EDITING',
  EDITING = 'EDITING',
}

export function MethodEditorTabSavingButtons({
  editingState,
  anyErrors,
  errorText,
  areTablesStillLoading,
  handleOnCancel,
  handleOnSubmit,
  handleOnEdit,
}: {
  editingState: MethodEditorTabEditingState;
  anyErrors: boolean;
  errorText: string;
  areTablesStillLoading: boolean;
  handleOnCancel: () => void;
  handleOnSubmit: () => void;
  handleOnEdit: () => void;
}): JSX.Element {
  return editingState === MethodEditorTabEditingState.EDITING ? (
    <div>
      <Button
        className={css(styles.detailsSaveButton)}
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        size={ButtonSize.MINI}
        text="Cancel"
        onClick={handleOnCancel}
        disabled={false}
        dataTestAttrs={{component: 'MethodEditorTabSavingButtons', element: 'cancel'}}
      />
      <FloatTooltip text={errorText} isDisabled={!anyErrors}>
        <Button
          className={css(styles.detailsSaveButton)}
          buttonStyle={ButtonStyle.PRIMARY}
          size={ButtonSize.MINI}
          text="Save"
          onClick={handleOnSubmit}
          disabled={anyErrors}
          dataTestAttrs={{component: 'MethodEditorTabSavingButtons', element: 'submit'}}
        />
      </FloatTooltip>
    </div>
  ) : (
    <FloatTooltip text="Please wait for tables to load" isDisabled={!areTablesStillLoading}>
      <Button
        className={css(styles.detailsSaveButton)}
        icon={editingState === MethodEditorTabEditingState.STARTING_EDITING ? undefined : 'edit'}
        buttonStyle={
          editingState === MethodEditorTabEditingState.STARTING_EDITING
            ? ButtonStyle.TERTIARY_GRAY
            : ButtonStyle.PRIMARY
        }
        size={ButtonSize.MINI}
        text={editingState === MethodEditorTabEditingState.STARTING_EDITING ? '...' : 'Edit'}
        onClick={handleOnEdit}
        disabled={areTablesStillLoading || editingState === MethodEditorTabEditingState.STARTING_EDITING}
        dataTestAttrs={{component: 'MethodEditorTabSavingButtons', element: 'edit'}}
      />
    </FloatTooltip>
  );
}

export const methodEditorHorizontalMargin = '25px';
export const methodEditorVerticalMargin = '16px';
export const styles = StyleSheet.create({
  detailsSaveButton: {
    flex: '0 0 auto',
    marginRight: '10px',
  },
  // Used in tabs that show the save/cancel buttons
  detailsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    marginRight: methodEditorHorizontalMargin,
  },
  detailsContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  detailsContent: {
    flex: '1 1 0',
    minHeight: '250px',
  },
  detailsContentScrollContainer: {
    overflowY: 'scroll',
    height: '100%',
    // Padding used instead of margins so that scrollbars render correctly
    paddingRight: methodEditorHorizontalMargin,
  },
});
